import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import useTranslation from '@/hooks/useTranslation';
import { useAnalysisSchemaStatus, useAnalysisRunProgress } from '@/reactQuery/researchAssistant';
import NonBlockingProgressStrip from '@/components/ui/NonBlockingProgressStrip';
import ComparisonHub from './ComparisonHub';
import ComparisonReport from './ComparisonReport';

interface CompareTabProps {
  analysisLanguage: string;
  // TELV1-312 — `force` bypasses the analyzer's "no new conversations"
  // early-return. The banner must pass `true` on COMPLETED projects;
  // without it the runner short-circuits to DONE and the rich schema
  // is never written. See runner.service.ts L209.
  onReRun?: (filterId?: string | null, force?: boolean) => Promise<void>;
  isRerunLoading?: boolean;
}

function CompareTab({ analysisLanguage, onReRun, isRerunLoading }: CompareTabProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const comparisonId = searchParams.get(SearchParam.COMPARISON_ID);

  // Pre-flight: does this project have the rich analysis schema the
  // Compare feature needs? Check is project-level (no filter) because
  // Compare targets multiple filters and the gate applies to the base
  // analysis pipeline.
  // D.8: replaced OutdatedAnalysisDialog with inline notice. The dialog is
  // deleted; per-tab inline notice replaces the modal blocking behaviour.
  const { data: schemaStatus } = useAnalysisSchemaStatus(projectId, null);
  const { data: runProgress } = useAnalysisRunProgress(projectId);
  const isRunInFlight =
    !!runProgress && runProgress.phase !== 'idle' && runProgress.phase !== 'complete';

  const schemaOutdated = !!schemaStatus && !schemaStatus.current;

  if (comparisonId) {
    return <ComparisonReport comparisonId={comparisonId} analysisLanguage={analysisLanguage} />;
  }

  return (
    <>
      {/* D.8 + TELV1-296 follow-up: advisory banner when the project's
          analysis predates the comparison feature. Nothing is running
          here — `staticIcon` swaps the spinner for a warning triangle
          (the spinner falsely implied "background work in progress" and
          QA reported it "never stops" on legacy projects).
          `actionLabel` + `onAction` now expose the re-run trigger the
          banner was missing — without it the user had no way to act on
          the message and the feature appeared stuck. The
          `!isRerunLoading` guard prevents a double-click race between
          this banner and the in-flight banner below. */}
      {schemaOutdated && !isRunInFlight && !isRerunLoading && (
        <div className="w-full pt-6">
          <NonBlockingProgressStrip
            variant="banner"
            title={lang.get('msg.compareNeedsRerun')}
            staticIcon
            actionLabel={onReRun ? lang.get('msg.reRunAnalysis') : undefined}
            onAction={onReRun ? () => onReRun(null, true) : undefined}
          />
        </div>
      )}

      {/* D.8: per-tab inline notice while a run is in flight.
          TELV1-296 follow-up: also active during the brief
          `isRerunLoading` window before the polled progress endpoint
          flips `phase` away from 'idle' — keeps the user informed during
          the click → backend-accept → first-poll latency window. */}
      {(isRunInFlight || isRerunLoading) && (
        <div className="w-full pt-6">
          <NonBlockingProgressStrip
            variant="banner"
            title={lang.get('msg.raBuildingRichSchemaRunning')}
            substep={runProgress?.phase !== 'idle' ? runProgress?.phase : undefined}
            current={runProgress?.current}
            total={runProgress?.total}
            startedAt={runProgress?.started_at}
          />
        </div>
      )}
      <ComparisonHub onReRun={onReRun} isRerunLoading={isRerunLoading} />
    </>
  );
}

export default CompareTab;
